import { Button } from 'primereact/button'
import { Checkbox } from 'primereact/checkbox'
import { Dialog } from 'primereact/dialog'
import { Dropdown } from 'primereact/dropdown'
import { InputText } from 'primereact/inputtext'
import { ProgressSpinner } from 'primereact/progressspinner'
import { classNames } from 'primereact/utils'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { apiClient } from '../../api/configs/axios-setup'
import { createGif, getGifJob, GifFrameInput, GifJob } from '../../api/gif-API'
import {
  applyMetricFilterParams,
  PostProcImage,
  PostProcImageFilters,
  PostProcImagesResponse
} from '../../api/submission-API'
import MetricMultiSelect from './MetricMultiSelect'

/** A case the user can browse for frames. */
export type GifCaseOption = {
  systemRunId: string
  caseName: string
  runId?: string
  projectName?: string
  runName?: string
}

type CreateGifModalProps = {
  isOpen: boolean
  onClose: () => void
  /** Cases available to browse in the left "Project - Run" panel. */
  cases: GifCaseOption[]
  /** When provided, shows a "Save to Run Media" button for the finished GIF. */
  onSaveToRunMedia?: (job: GifJob) => void | Promise<void>
  savingToRunMedia?: boolean
}

/** A source frame with the metadata needed for labels + ordering. */
type SelectedFrame = {
  key: string
  url: string
  caseName: string
  systemRunId: string
  metricType: string
  location: string
  isBaseline: boolean
}

const SPEED_OPTIONS = [
  { label: '0.1x', delayMs: 5000 },
  { label: '0.25x', delayMs: 2000 },
  { label: '0.5x', delayMs: 1000 },
  { label: '1x', delayMs: 500 },
  { label: '2x', delayMs: 250 },
  { label: '4x', delayMs: 125 }
]

const POLL_INTERVAL_MS = 1500
const MAX_IMAGES_PER_CASE = 200

type ImageFetchFilters = {
  imageType?: string
  section?: string
  location?: string
  /** Plain metrics, or "{metric}||{referenceRunId}" entries for compare deltas. */
  metricTypes?: string[]
}

const fetchCaseImages = async (
  systemRunId: string,
  filters: ImageFetchFilters = {}
): Promise<PostProcImagesResponse> => {
  const params = new URLSearchParams()
  params.set('skip', '0')
  params.set('limit', String(MAX_IMAGES_PER_CASE))
  if (filters.imageType) params.set('imageType', filters.imageType)
  if (filters.section) params.set('section', filters.section)
  if (filters.location) params.set('location', filters.location)
  applyMetricFilterParams(params, filters.metricTypes)
  const res = await apiClient.get<PostProcImagesResponse>(
    `/WorkflowSubmissions/cases/${encodeURIComponent(systemRunId)}/images?${params.toString()}`
  )
  return res.data
}

const CreateGifModal: FC<CreateGifModalProps> = ({
  isOpen,
  onClose,
  cases,
  onSaveToRunMedia,
  savingToRunMedia
}) => {
  const [search, setSearch] = useState('')
  const [checkedCaseIds, setCheckedCaseIds] = useState<Set<string>>(new Set())
  const [imagesByCase, setImagesByCase] = useState<Record<string, PostProcImage[]>>({})
  const [filtersByCase, setFiltersByCase] = useState<Record<string, PostProcImageFilters>>({})
  const [loadingCases, setLoadingCases] = useState<Set<string>>(new Set())

  // Filters mirror the standard gallery view. Default to All Types so images from any
  // selected case show immediately (cases may have only Section or only Surface plots).
  const [imageTypeFilter, setImageTypeFilter] = useState<string | null>(null)
  const [sectionFilter, setSectionFilter] = useState<string | null>(null)
  const [locationFilter, setLocationFilter] = useState<string | null>(null)
  const [metricFilters, setMetricFilters] = useState<string[]>([])

  const [selectedFrames, setSelectedFrames] = useState<SelectedFrame[]>([])
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)

  const [speedDelay, setSpeedDelay] = useState(500)
  const [loop, setLoop] = useState(true)
  const [gifName, setGifName] = useState('')
  const [gifLabel, setGifLabel] = useState('')

  const [job, setJob] = useState<GifJob | null>(null)
  const [generating, setGenerating] = useState(false)
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Client-side speed preview (cycles the selected frames before generating).
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewIdx, setPreviewIdx] = useState(0)

  // Reset everything when the modal closes.
  useEffect(() => {
    if (!isOpen) {
      setSearch('')
      setCheckedCaseIds(new Set())
      setImagesByCase({})
      setFiltersByCase({})
      setLoadingCases(new Set())
      setImageTypeFilter(null)
      setSectionFilter(null)
      setLocationFilter(null)
      setMetricFilters([])
      setSelectedFrames([])
      setJob(null)
      setGenerating(false)
      setPreviewOpen(false)
      setGifLabel('')
      if (pollRef.current) clearTimeout(pollRef.current)
    }
  }, [isOpen])

  useEffect(() => {
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current)
    }
  }, [])

  // Toggle a case; images load via the effect below whenever the selection or filters change.
  const toggleCase = (option: GifCaseOption) => {
    setCheckedCaseIds(prev => {
      const next = new Set(prev)
      if (next.has(option.systemRunId)) next.delete(option.systemRunId)
      else next.add(option.systemRunId)
      return next
    })
  }

  // Fetch each checked case's images with the current filters applied server-side. This keeps
  // the grid correct regardless of the per-case image cap and lets deltas load on demand.
  useEffect(() => {
    if (!isOpen || checkedCaseIds.size === 0) return
    let cancelled = false
    checkedCaseIds.forEach(async id => {
      setLoadingCases(prev => new Set(prev).add(id))
      try {
        const res = await fetchCaseImages(id, {
          imageType: imageTypeFilter ?? undefined,
          section: sectionFilter ?? undefined,
          location: locationFilter ?? undefined,
          metricTypes: metricFilters
        })
        if (cancelled) return
        setImagesByCase(prev => ({ ...prev, [id]: res.images ?? [] }))
        setFiltersByCase(prev => ({ ...prev, [id]: res.availableFilters }))
      } catch {
        if (!cancelled) toast.error('Failed to load images')
      } finally {
        setLoadingCases(prev => {
          const s = new Set(prev)
          s.delete(id)
          return s
        })
      }
    })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, checkedCaseIds, imageTypeFilter, sectionFilter, locationFilter, metricFilters])

  // All images from checked cases.
  const activeImages = useMemo(() => {
    const out: Array<{ image: PostProcImage; option: GifCaseOption }> = []
    cases.forEach(option => {
      if (!checkedCaseIds.has(option.systemRunId)) return
      ;(imagesByCase[option.systemRunId] ?? []).forEach(image => out.push({ image, option }))
    })
    return out
  }, [cases, checkedCaseIds, imagesByCase])

  // Dropdown options come from each case's availableFilters (computed server-side over ALL
  // images), unioned across checked cases — so every type/location/metric shows even though
  // only a page of images is loaded at a time.
  const mergedFilters = useMemo(() => {
    const imageTypes = new Set<string>()
    const sections = new Set<string>()
    const locations = new Set<string>()
    const metricTypes = new Set<string>()
    const referenceMetrics = new Map<string, { metricType: string; referenceRunId: string }>()
    cases.forEach(option => {
      if (!checkedCaseIds.has(option.systemRunId)) return
      const f = filtersByCase[option.systemRunId]
      if (!f) return
      f.imageTypes?.forEach(t => imageTypes.add(t))
      f.sections?.forEach(s => sections.add(s))
      f.locations?.forEach(l => locations.add(l))
      f.metricTypes?.forEach(m => metricTypes.add(m))
      f.referenceMetrics?.forEach(rm =>
        referenceMetrics.set(`${rm.metricType}||${rm.referenceRunId}`, rm)
      )
    })
    return { imageTypes, sections, locations, metricTypes, referenceMetrics }
  }, [cases, checkedCaseIds, filtersByCase])

  const imageTypeOptions = useMemo(
    () => [
      { label: 'All Types', value: null },
      ...Array.from(mergedFilters.imageTypes)
        .sort()
        .map(t => ({ label: t === 'Sect' ? 'Section' : t, value: t }))
    ],
    [mergedFilters]
  )

  const sectionOptions = useMemo(
    () => [
      { label: 'All Sect.', value: null },
      ...Array.from(mergedFilters.sections)
        .sort()
        .map(s => ({ label: s, value: s }))
    ],
    [mergedFilters]
  )

  const locationOptions = useMemo(
    () => [
      { label: 'All Loc.', value: null },
      ...Array.from(mergedFilters.locations)
        .sort()
        .map(l => ({ label: l, value: l }))
    ],
    [mergedFilters]
  )

  // Metric options mirror the gallery: plain baseline metrics plus compare deltas encoded
  // as "{metricType}||{referenceRunId}" (e.g. "dCp||323") so the filter can match both.
  const metricOptions = useMemo(() => {
    const compareOptions = Array.from(mergedFilters.referenceMetrics.values())
      .sort(
        (a, b) =>
          a.metricType.localeCompare(b.metricType) ||
          a.referenceRunId.localeCompare(b.referenceRunId)
      )
      .map(rm => ({
        label: `${rm.metricType} (${rm.referenceRunId})`,
        value: `${rm.metricType}||${rm.referenceRunId}`
      }))
    return [
      ...Array.from(mergedFilters.metricTypes)
        .sort()
        .map(m => ({ label: m, value: m })),
      ...compareOptions
    ]
  }, [mergedFilters])

  // Images are filtered server-side (see the fetch effect), so show them as-is.
  const filteredImages = activeImages

  const frameKey = (systemRunId: string, url: string) => `${systemRunId}::${url}`

  const isFrameSelected = (systemRunId: string, url: string) =>
    selectedFrames.some(f => f.key === frameKey(systemRunId, url))

  const toggleFrame = (image: PostProcImage, option: GifCaseOption) => {
    const key = frameKey(option.systemRunId, image.url)
    setSelectedFrames(prev => {
      if (prev.some(f => f.key === key)) return prev.filter(f => f.key !== key)
      return [
        ...prev,
        {
          key,
          url: image.url,
          caseName: option.caseName,
          systemRunId: option.systemRunId,
          metricType: image.metadata.metricType ?? '',
          location: image.metadata.location ?? '',
          isBaseline: !image.metadata.referenceRunId
        }
      ]
    })
  }

  const removeFrame = (key: string) => setSelectedFrames(prev => prev.filter(f => f.key !== key))

  const reorderFrame = (from: number, to: number) => {
    setSelectedFrames(prev => {
      if (from === to || from < 0 || to < 0 || from >= prev.length || to >= prev.length) return prev
      const next = [...prev]
      const [moved] = next.splice(from, 1)
      next.splice(to, 0, moved)
      return next
    })
  }

  const filteredCases = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return cases
    return cases.filter(
      c =>
        c.caseName.toLowerCase().includes(q) ||
        c.runName?.toLowerCase().includes(q) ||
        c.projectName?.toLowerCase().includes(q)
    )
  }, [cases, search])

  // Group cases by project for the tree.
  const groupedCases = useMemo(() => {
    const groups = new Map<string, GifCaseOption[]>()
    filteredCases.forEach(c => {
      const key = c.projectName ?? 'Cases'
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key)!.push(c)
    })
    return Array.from(groups.entries())
  }, [filteredCases])

  const pollJob = (jobId: string) => {
    pollRef.current = setTimeout(async () => {
      try {
        const updated = await getGifJob(jobId)
        setJob(updated)
        if (updated.status === 'completed' || updated.status === 'failed') {
          setGenerating(false)
          if (updated.status === 'failed') {
            toast.error(updated.error || 'GIF generation failed')
          }
          return
        }
        pollJob(jobId)
      } catch {
        pollJob(jobId)
      }
    }, POLL_INTERVAL_MS)
  }

  const handleGenerate = async () => {
    if (selectedFrames.length < 2) {
      toast.error('Select at least two frames to create a GIF.')
      return
    }
    setGenerating(true)
    setJob(null)
    const frames: GifFrameInput[] = selectedFrames.map(f => ({
      url: f.url,
      caseId: f.systemRunId,
      metricType: f.metricType,
      location: f.location,
      label: `${f.caseName} · ${f.location}`
    }))
    try {
      const created = await createGif(frames, {
        delayMs: speedDelay,
        loop,
        name: gifName.trim() || undefined,
        label: gifLabel.trim() || undefined
      })
      setJob(created)
      pollJob(created.jobId)
    } catch (err: any) {
      setGenerating(false)
      toast.error(err?.response?.data?.error || 'Failed to start GIF generation.')
    }
  }

  // Preview the chosen frames + speed locally so the user can tune the speed
  // before committing to a generation.
  const handlePreview = () => {
    if (selectedFrames.length < 2) {
      toast.error('Select at least two frames to preview.')
      return
    }
    setPreviewIdx(0)
    setPreviewOpen(true)
  }

  // Changing the speed invalidates a generated GIF — force a regenerate.
  const handleSpeedChange = (delayMs: number) => {
    setSpeedDelay(delayMs)
    if (job) setJob(null)
  }

  // Discard the generated GIF so the speed unlocks and can be regenerated.
  const handleRegenerate = () => {
    setJob(null)
  }

  // Animate the preview overlay at the selected speed.
  useEffect(() => {
    if (!previewOpen || selectedFrames.length < 2) return
    let idx = 0
    setPreviewIdx(0)
    const timer = setInterval(() => {
      idx += 1
      if (idx >= selectedFrames.length) {
        if (!loop) {
          clearInterval(timer)
          return
        }
        idx = 0
      }
      setPreviewIdx(idx)
    }, speedDelay)
    return () => clearInterval(timer)
  }, [previewOpen, speedDelay, loop, selectedFrames])

  // Fetch the blob so the browser downloads it; the `download` attribute is ignored for the
  // cross-origin Azure blob URL and would otherwise just open the GIF in a new tab.
  const handleDownload = async () => {
    if (!job?.resultUrl) return
    try {
      const res = await fetch(job.resultUrl)
      const blob = await res.blob()
      const objectUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = objectUrl
      link.download = `${job.name || 'animation'}.gif`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(objectUrl)
    } catch {
      toast.error('Failed to download GIF')
    }
  }

  const activeMetricLabel = metricFilters.length
    ? metricFilters.map(v => metricOptions.find(o => o.value === v)?.label ?? v).join(', ')
    : 'all metrics'
  const emptyStateSub = `Images matching ${activeMetricLabel} / ${
    locationFilter ?? 'all views'
  } will appear here.`

  return (
    <Dialog
      visible={isOpen}
      onHide={onClose}
      header="Create Animated GIF"
      style={{ width: '1040px', maxWidth: '95vw' }}
      modal
      dismissableMask
      pt={{ content: { className: 'p-0' } }}>
      <div className="flex gap-3 p-1">
        {/* Left: Project - Run tree */}
        <div className="w-[190px] shrink-0 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-neutral-90 p-3 flex flex-col">
          <p className="text-sm font-bold text-slate-600 dark:text-neutral-50 mb-2">
            Project - Run
          </p>
          <span className="p-input-icon-left mb-2">
            <i className="pi pi-search !left-4" />
            <InputText
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search"
              className="w-full text-xs !pl-9"
            />
          </span>
          <div className="overflow-y-auto flex-1 min-h-[360px] max-h-[420px] pr-1">
            {groupedCases.length === 0 && (
              <p className="text-xs text-gray-400 mt-2">No cases available.</p>
            )}
            {groupedCases.map(([project, options]) => (
              <div key={project} className="mb-2">
                {project !== 'Cases' && (
                  <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    {project}
                  </p>
                )}
                {options.map(option => (
                  <label
                    key={option.systemRunId}
                    className="flex items-center gap-2 py-1 pl-2 cursor-pointer text-xs text-gray-800 dark:text-neutral-10 hover:bg-gray-50 dark:hover:bg-neutral-80 rounded">
                    <Checkbox
                      checked={checkedCaseIds.has(option.systemRunId)}
                      onChange={() => toggleCase(option)}
                    />
                    <span className="truncate">{option.runName ?? option.caseName}</span>
                    {loadingCases.has(option.systemRunId) && (
                      <i className="pi pi-spin pi-spinner text-[10px] text-gray-400" />
                    )}
                  </label>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Right: content + selection + actions */}
        <div className="flex-1 min-w-0 flex flex-col">
          {/* Filters — mirror the standard gallery view */}
          {checkedCaseIds.size > 0 && (
            <div className="flex items-center gap-1 px-1 pb-3 flex-wrap">
              {/* Image type filter */}
              <Dropdown
                value={imageTypeFilter}
                options={imageTypeOptions}
                optionLabel="label"
                optionValue="value"
                onChange={e => {
                  setImageTypeFilter(e.value)
                  // Reset dependent filters when the image type changes.
                  setSectionFilter(null)
                  setLocationFilter(null)
                  setMetricFilters([])
                }}
                placeholder="Type"
                appendTo="self"
                className="text-xs w-36"
              />

              {/* Section filter (only for Sect type) */}
              {imageTypeFilter === 'Sect' && (
                <Dropdown
                  value={sectionFilter}
                  options={sectionOptions}
                  optionLabel="label"
                  optionValue="value"
                  onChange={e => setSectionFilter(e.value)}
                  placeholder="Section"
                  appendTo="self"
                  className="text-xs w-36"
                />
              )}

              {/* Location filter (only for Surface type) */}
              {imageTypeFilter === 'Surface' && (
                <Dropdown
                  value={locationFilter}
                  options={locationOptions}
                  optionLabel="label"
                  optionValue="value"
                  onChange={e => setLocationFilter(e.value)}
                  placeholder="Location"
                  appendTo="self"
                  className="text-xs w-36"
                />
              )}

              {/* Metric type filter (multi-select) */}
              <MetricMultiSelect
                value={metricFilters}
                options={metricOptions}
                onChange={setMetricFilters}
                appendTo="self"
                className="text-xs w-36"
              />
            </div>
          )}

          {/* Image grid / empty state */}
          <div className="min-h-[180px] max-h-[300px] overflow-y-auto px-1">
            {filteredImages.length === 0 ? (
              <div className="pt-8 text-center">
                <p className="text-sm font-medium text-gray-400">
                  {checkedCaseIds.size === 0
                    ? 'Select a CFD case from the left panel'
                    : 'No matching images'}
                </p>
                <p className="text-xs text-gray-300 mt-1">{emptyStateSub}</p>
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-3">
                {filteredImages.map(({ image, option }) => {
                  const selected = isFrameSelected(option.systemRunId, image.url)
                  return (
                    <button
                      key={frameKey(option.systemRunId, image.url)}
                      type="button"
                      onClick={() => toggleFrame(image, option)}
                      className={classNames(
                        'relative rounded-lg overflow-hidden border-2 transition-all bg-gray-100 dark:bg-neutral-80',
                        selected
                          ? 'border-blue-500 ring-2 ring-blue-300'
                          : 'border-transparent hover:border-gray-300'
                      )}>
                      <img
                        src={image.url}
                        alt={image.name}
                        className="w-full h-[86px] object-cover"
                        loading="lazy"
                      />
                      {selected && (
                        <span className="absolute top-1 right-1 bg-blue-600 text-white rounded-full w-4 h-4 flex items-center justify-center text-[9px]">
                          <i className="pi pi-check text-[8px]" />
                        </span>
                      )}
                      <span className="block truncate px-1 py-0.5 text-[10px] text-gray-500 dark:text-neutral-50">
                        {option.caseName} · {image.metadata.location}
                      </span>
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          {/* Selected for GIF strip */}
          <div className="mt-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-neutral-90 rounded-b-lg px-3 py-3">
            <div className="flex items-center gap-2 mb-2">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">
                Selected for GIF
              </p>
              <span className="bg-blue-100 text-blue-700 rounded-full px-1.5 text-[10px]">
                {selectedFrames.length}
              </span>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {selectedFrames.map((frame, index) => (
                <div
                  key={frame.key}
                  draggable
                  onDragStart={() => setDragIndex(index)}
                  onDragOver={e => {
                    e.preventDefault()
                    if (dragOverIndex !== index) setDragOverIndex(index)
                  }}
                  onDrop={e => {
                    e.preventDefault()
                    if (dragIndex !== null) reorderFrame(dragIndex, index)
                    setDragIndex(null)
                    setDragOverIndex(null)
                  }}
                  onDragEnd={() => {
                    setDragIndex(null)
                    setDragOverIndex(null)
                  }}
                  className={classNames(
                    'group relative shrink-0 w-[76px] rounded-lg border-[1.5px] bg-white dark:bg-neutral-80 shadow-sm overflow-hidden cursor-grab active:cursor-grabbing transition-all',
                    dragOverIndex === index && dragIndex !== index
                      ? 'border-blue-500 ring-2 ring-blue-300'
                      : 'border-green-300',
                    dragIndex === index ? 'opacity-40' : ''
                  )}>
                  <img
                    src={frame.url}
                    alt=""
                    className="w-full h-[44px] object-cover pointer-events-none"
                  />
                  <span className="absolute top-0.5 left-1 text-[8px] text-white/80 font-mono">
                    {index + 1}
                  </span>
                  {frame.isBaseline && (
                    <span className="absolute top-0.5 right-1 bg-black/65 text-green-400 rounded px-1 text-[7px] font-bold">
                      BL
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => removeFrame(frame.key)}
                    title="Remove frame"
                    className="absolute top-0.5 right-1 hidden group-hover:flex items-center justify-center w-3.5 h-3.5 rounded-full bg-black/65 text-white text-[7px] hover:bg-red-500">
                    <i className="pi pi-times text-[7px]" />
                  </button>
                  <div className="px-1 py-0.5">
                    <p className="text-[8px] text-gray-500 truncate">{frame.metricType}</p>
                    <p className="text-[7px] text-gray-400 truncate">
                      {frame.systemRunId} · {frame.location}
                    </p>
                  </div>
                  <div className="flex items-center justify-center pb-0.5 text-gray-300">
                    <i className="pi pi-bars text-[8px]" title="Drag to reorder" />
                  </div>
                </div>
              ))}
              <div className="shrink-0 w-[76px] h-[72px] rounded-lg border-[1.5px] border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-300">
                <i className="pi pi-plus text-xs" />
                <span className="text-[8px] mt-1">Add more</span>
              </div>
            </div>
          </div>

          {/* Settings + actions */}
          <div className="mt-3 flex items-center justify-between gap-4 flex-wrap px-1">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-500">Speed</span>
                <select
                  value={speedDelay}
                  onChange={e => handleSpeedChange(Number(e.target.value))}
                  disabled={job?.status === 'completed'}
                  title={
                    job?.status === 'completed'
                      ? 'Regenerate to change the speed'
                      : 'Playback speed'
                  }
                  className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs bg-white dark:bg-neutral-90 disabled:opacity-60 disabled:cursor-not-allowed">
                  {SPEED_OPTIONS.map(o => (
                    <option key={o.label} value={o.delayMs}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <label
                className="flex items-center gap-1 text-xs text-gray-500 cursor-pointer"
                title="Loop the preview animation">
                <Checkbox checked={loop} onChange={e => setLoop(e.checked ?? false)} />
                Loop
              </label>
              <InputText
                value={gifName}
                onChange={e => setGifName(e.target.value)}
                placeholder="GIF name (optional)"
                className="text-xs"
              />
              <InputText
                value={gifLabel}
                onChange={e => setGifLabel(e.target.value)}
                placeholder="Label on GIF (optional)"
                maxLength={200}
                disabled={job?.status === 'completed'}
                title={
                  job?.status === 'completed'
                    ? 'Regenerate to change the label'
                    : 'Text shown in the top-left of every frame of the GIF; leave blank for no label'
                }
                className="text-xs"
              />
            </div>

            <div className="flex items-center gap-3">
              {job && job.status !== 'completed' && job.status !== 'failed' && (
                <span className="flex items-center gap-2 text-xs text-gray-500">
                  <ProgressSpinner style={{ width: 18, height: 18 }} strokeWidth="6" />
                  {job.status === 'pending' ? 'Queued…' : 'Generating…'}
                </span>
              )}
              {job?.status === 'failed' && (
                <span className="text-xs text-red-500">Failed — try again</span>
              )}
              {job?.status === 'completed' ? (
                <>
                  <Button
                    label="Preview"
                    icon="pi pi-eye"
                    onClick={handlePreview}
                    tooltip="Preview playback at the selected speed"
                    tooltipOptions={{ position: 'top' }}
                    className="!bg-white !text-xs !py-2 !px-3"
                    pt={{
                      root: { style: { border: '1px solid #0072CE' } },
                      label: { style: { color: '#0072CE' } },
                      icon: { style: { color: '#0072CE', marginRight: '0.5rem' } }
                    }}
                  />
                  <Button
                    label="Regenerate"
                    icon="pi pi-refresh"
                    onClick={handleRegenerate}
                    tooltip="Discard this GIF to change the speed"
                    tooltipOptions={{ position: 'top' }}
                    className="!bg-white !text-xs !py-2 !px-3"
                    pt={{
                      root: { style: { border: '1px solid #6b7280' } },
                      label: { style: { color: '#374151' } },
                      icon: { style: { color: '#374151', marginRight: '0.5rem' } }
                    }}
                  />
                  <Button
                    label="Download GIF"
                    icon="pi pi-download"
                    onClick={handleDownload}
                    className="!bg-gm-blue !border-gm-blue !text-white !text-xs !py-2 !px-3"
                    pt={{ icon: { style: { marginRight: '0.5rem' } } }}
                  />
                  {onSaveToRunMedia && (
                    <Button
                      label="Save to Run Media"
                      icon="pi pi-save"
                      loading={savingToRunMedia}
                      disabled={!job?.resultUrl || savingToRunMedia}
                      onClick={() => job && onSaveToRunMedia(job)}
                      tooltip="Save this GIF to the run's media library"
                      tooltipOptions={{ position: 'top' }}
                      className="!bg-gm-blue !border-gm-blue !text-white !text-xs !py-2 !px-3"
                      pt={{ icon: { style: { marginRight: '0.5rem' } } }}
                    />
                  )}
                </>
              ) : (
                <>
                  <Button
                    label="Preview"
                    icon="pi pi-eye"
                    onClick={handlePreview}
                    disabled={selectedFrames.length < 2}
                    tooltip={
                      selectedFrames.length < 2
                        ? 'Select at least two frames to preview'
                        : 'Preview playback at the selected speed'
                    }
                    tooltipOptions={{ position: 'top' }}
                    className="!bg-white !text-xs !py-2 !px-3"
                    pt={{
                      root: { style: { border: '1px solid #0072CE' } },
                      label: { style: { color: '#0072CE' } },
                      icon: { style: { color: '#0072CE', marginRight: '0.5rem' } }
                    }}
                  />
                  <Button
                    label="Generate GIF"
                    icon="pi pi-play"
                    loading={generating}
                    disabled={generating || selectedFrames.length < 2}
                    onClick={handleGenerate}
                    tooltip={
                      selectedFrames.length < 2
                        ? 'Select at least two frames to generate'
                        : undefined
                    }
                    tooltipOptions={{ position: 'top' }}
                    className="!bg-gray-700 !border-gray-700 !text-white !text-xs !py-2 !px-3"
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Client-side speed preview */}
      <Dialog
        header={`Preview · ${SPEED_OPTIONS.find(o => o.delayMs === speedDelay)?.label ?? ''}`}
        visible={previewOpen}
        onHide={() => setPreviewOpen(false)}
        style={{ width: 'min(90vw, 640px)' }}
        modal
        dismissableMask>
        {selectedFrames.length >= 2 ? (
          <div className="flex flex-col items-center gap-3">
            <div className="relative">
              <img
                src={selectedFrames[Math.min(previewIdx, selectedFrames.length - 1)]?.url}
                alt="GIF preview frame"
                className="max-h-[60vh] w-auto object-contain rounded border border-gray-200 dark:border-neutral-70 bg-black"
              />
              {gifLabel.trim() && (
                <span className="absolute left-2 top-2 max-w-[92%] truncate rounded bg-black/60 px-2 py-1 text-xs font-semibold text-white">
                  {gifLabel.trim()}
                </span>
              )}
            </div>
            <div className="text-xs text-gray-500">
              Frame {Math.min(previewIdx, selectedFrames.length - 1) + 1} / {selectedFrames.length}{' '}
              · adjust the Speed to preview a different pace before generating.
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500">Select at least two frames to preview.</p>
        )}
      </Dialog>
    </Dialog>
  )
}

export default CreateGifModal
